import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { StudentInfo } from "../student/studentSlice";
import { getBackendUrl, LoadStateInfo, loaded, loading, notStarted, error, failedResponse } from "../common";
import { getToken } from "../../features/user/UserAvatar";

export interface SchoolStudentsState {
    state: LoadStateInfo;
    list?: StudentInfo[];
    request?: RetrieveSchoolStudentsRequest;
}

const initialState: SchoolStudentsState = {
    state: notStarted(),
};

export interface RetrieveSchoolStudentsRequest {
    schoolId?: string;
    classroomId?: string;
    filter?: string;
    getClassroom?: boolean;
    getFamily?: boolean;
}
export const retrieveSchoolStudents = createAsyncThunk(
    "retrieveSchoolStudents",
    async (params: RetrieveSchoolStudentsRequest, thunkAPI) => {
        console.debug(`retrieve student options for classroom ${params.classroomId} at school ${params.schoolId}`);

        let query = `classroomId=${params.classroomId}`;
        if (params.filter) {
            query += `&filter=${params.filter}`;
        }
        if (params.getClassroom) {
            query += `&getClassroom=${params.getClassroom}`;
        }
        if (params.getFamily) {
            query += `&getFamily=${params.getFamily}`;
        }

        const response = await fetch(`${getBackendUrl()}/api/school/${params.schoolId}/student?${query}`, {
            signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${getToken()}`,
            }
        });

        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    }
);


export const schoolStudentsSlice = createSlice({
    name: "schoolStudents",
    initialState,
    // The `reducers` field lets us define reducers and generate associated actions
    reducers: {
        add: (state, action: PayloadAction<StudentInfo>) => {
            if (state.list) {
                state.list.push(action.payload);
                state.list.sort((a, b) => (a?.name || "").localeCompare(b?.name || ""));
            } else {
                console.error("Trying to add student to an empty list");
            }
        },
        remove: (state, action: PayloadAction<StudentInfo>) => {
            if (state.list) {
                state.list = state.list.filter((student) => student.id !== action.payload.id);
            } else {
                console.error("Trying to remove student from an empty list");
            }
        },
        // setFilter: (state, action: PayloadAction<string>) => {
        //     state.filter = action.payload;
        // },
        check: (state, action: PayloadAction<RetrieveSchoolStudentsRequest>) => {
            const data = action.payload;
            const request = state.request;
            console.log(`Check ${data.schoolId} != ${request?.schoolId}, ${data.classroomId} != ${request?.classroomId}, ${data.filter} != ${request?.filter}`);
            if (`${request?.schoolId}` != `${data.schoolId}`
                || `${request?.classroomId}` != `${data.classroomId}`
                || `${request?.filter}` != `${data.filter}`
            ) {
                console.log("changed");
                state.state = notStarted();
                state.list = undefined;
            } else {
                console.log(`unchanged :: ${state.state.state}`);
            }
        },
        clean: (state) => {
            state.state = notStarted();
            state.list = undefined;
        }
    },
    // The `extraReducers` field lets the slice handle actions defined elsewhere,
    // including actions generated by createAsyncThunk or in other slices.
    extraReducers: (builder) => {
        builder
            .addCase(retrieveSchoolStudents.pending, (state, action) => {
                state.state = loading();
                state.list = undefined;
                state.request = action.meta.arg;
            })
            .addCase(retrieveSchoolStudents.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                state.state = loaded();
                state.list = action.payload;
                state.list?.sort((a, b) => (a?.name || "").localeCompare(b?.name || ""));
            })
            .addCase(retrieveSchoolStudents.rejected, (state, action) => {
                state.state = error(action);
            });
    },
});

export const { } = schoolStudentsSlice.actions;

export const selectSchoolStudents = (state: RootState) => state.schoolStudents;

export default schoolStudentsSlice.reducer;
